/**
 * 获取 URL 参数
 * @param name 参数名
 * @returns 参数值或null
 */
export const getUrlParam = (name: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get(name)
}

/**
 * 移除 URL 中的指定参数
 * @param paramNames 要移除的参数名数组
 */
export const removeUrlParams = (paramNames: string[]): void => {
  const url = new URL(window.location.href)

  paramNames.forEach(param => {
    url.searchParams.delete(param)
  })

  // 更新浏览器历史记录，不刷新页面
  window.history.replaceState({}, '', url.toString())
}

/**
 * 清除认证相关的 URL 参数
 */
export const clearAuthParams = (): void => {
  removeUrlParams(['code', 'state', 'error'])
}

/**
 * 清除所有认证和导航相关的URL参数
 * 包括 code, state, error, from, source 等
 */
export const clearAllAuthParams = (): void => {
  // 方案2：redirect 会在进入登录页时缓存到 sessionStorage，这里可清理 URL 保持干净
  removeUrlParams(['code', 'state', 'error', 'from', 'source', 'redirect'])
}

/**
 * 是否为 http(s) 绝对地址
 */
export const isHttpUrl = (value: string): boolean => {
  return /^https?:\/\//i.test(value)
}

/**
 * 允许外部跳转的 host 白名单（逗号分隔）
 * - 例如：VITE_REDIRECT_ALLOW_HOSTS="vas-test.xxx.com,vas.xxx.com"
 * - 默认允许当前站点 host
 */
export const getRedirectAllowHosts = (): string[] => {
  const raw = import.meta.env.VITE_REDIRECT_ALLOW_HOSTS as string | undefined
  const hosts = (raw || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)

  return Array.from(new Set([window.location.host, ...hosts]))
}

/**
 * 外部 redirect 是否允许（防开放重定向）
 */
export const isAllowedExternalRedirect = (redirectUrl: string): boolean => {
  try {
    const url = new URL(redirectUrl)
    if (!/^https?:$/i.test(url.protocol)) return false
    return getRedirectAllowHosts().includes(url.host)
  } catch {
    return false
  }
}

/**
 * 将 token 注入到 URL：
 * - 若包含 {token} 占位符：替换
 * - 否则追加 token 查询参数（不覆盖已有 token）
 */
export const injectTokenToUrl = (targetUrl: string, token: string): string => {
  if (!token) return targetUrl
  if (targetUrl.includes('{token}')) {
    return targetUrl.replace('{token}', encodeURIComponent(token))
  }

  try {
    const url = new URL(targetUrl)
    if (!url.searchParams.has('token')) {
      url.searchParams.set('token', token)
    }
    return url.toString()
  } catch {
    return targetUrl
  }
}

/**
 * 登录成功后按 redirect 回跳（方式A：redirect URL 带 token）
 */
export const navigateToExternalRedirectWithToken = (redirectUrl: string | undefined, token: string): boolean => {
  const raw = (redirectUrl || '').trim()
  // 兼容：redirect 可能是 encodeURIComponent 后的字符串，这里尝试解一次
  const normalized = (() => {
    if (!raw) return raw
    if (/^https?%3A%2F%2F/i.test(raw)) {
      try {
        return decodeURIComponent(raw)
      } catch {
        return raw
      }
    }
    return raw
  })()

  const tokenExists = !!token
  const allowed = normalized ? isAllowedExternalRedirect(normalized) : false

  if (!normalized || !isHttpUrl(normalized) || !allowed) {
    // 仅用于排查：为什么 redirect 没有触发外跳
    console.warn('[TP_EXTERNAL_REDIRECT_FAIL]', {
      redirectUrl,
      normalized,
      tokenExists,
      isHttpUrl: normalized ? isHttpUrl(normalized) : false,
      allowed,
      allowHosts: getRedirectAllowHosts()
    })
    return false
  }

  const finalUrl = injectTokenToUrl(normalized, token)
  window.location.href = finalUrl
  return true
}

/**
 * 检查URL中是否包含认证参数
 * @returns boolean 是否包含认证参数
 */
export const hasAuthParams = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.has('code') || urlParams.has('state') || urlParams.has('error')
}
